// Booking Status System Types - Database-driven, not hardcoded
import { BookingStatus } from '@/hooks/useBookingStatuses';

// Generic types that work with any status from database
export type BookingStatusKey = string; // Any valid status from booking_statuses table
export type BookingStatusCategory = 'booking' | 'quotation' | 'payment' | 'fulfillment';
export type BookingStatusDisplayName = string; // Any display name from database

// Complete booking status interface matching database structure  
export interface BookingStatusDefinition {
  status_key: string;
  display_name: string;
  description: string;
  color: string; // Hex color code
  sort_order: number;
  category: BookingStatusCategory;
  is_active: boolean;
  created_at: string;
}

// Status transition interface - REMOVED
// Open transitions architecture: any from_status -> to_status is allowed
// Only event_flows define which transitions trigger communications

// Grouped statuses by category for UI organization
export interface BookingStatusesByCategory {
  booking: BookingStatusDefinition[];
  quotation: BookingStatusDefinition[];
  payment: BookingStatusDefinition[];
  fulfillment: BookingStatusDefinition[];
}

// Event flow mapping interface for centralized event system
export interface EventFlowDefinition {
  id: string;
  from_status: string | null; // NULL means "any status" (e.g., creation events)
  to_status: string;
  channels: CommunicationChannel[];
  is_active: boolean;
  delay_minutes: number;
  conditions: Record<string, any>;
}

// Communication channels for event system
export type CommunicationChannel = 
  | 'email'
  | 'sms' 
  | 'whatsapp'
  | 'push';

// Template types based on categories
export type CommunicationTemplate = BookingStatusCategory;

// Runtime helper functions that use actual database data
export const getStatusCategory = (
  status: string, 
  statuses: BookingStatus[] // Use the hook's interface
): BookingStatusCategory | null => {
  const statusDef = statuses.find(s => s.status_key === status);
  return statusDef?.category as BookingStatusCategory || null;
};

export const getStatusColor = (
  status: string, 
  statuses: BookingStatus[] // Use the hook's interface
): string => {
  const statusDef = statuses.find(s => s.status_key === status);
  return statusDef?.color || '#9CA3AF'; // Default gray
};

export const getStatusDisplayName = (
  status: string,
  statuses: BookingStatus[] // Use the hook's interface
): string => {
  const statusDef = statuses.find(s => s.status_key === status);
  return statusDef?.display_name || status.charAt(0).toUpperCase() + status.slice(1);
};

export const getStatusesByCategory = (
  statuses: BookingStatus[] // Use the hook's interface
): BookingStatusesByCategory => {
  return statuses.reduce((acc, status) => {
    const category = status.category as BookingStatusCategory;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(status as BookingStatusDefinition);
    return acc;
  }, {
    booking: [],
    quotation: [],
    payment: [],
    fulfillment: []
  } as BookingStatusesByCategory);
};

export const isValidStatus = (
  status: string,
  statuses: BookingStatus[] // Use the hook's interface
): boolean => {
  // Since get_valid_booking_statuses only returns active statuses, 
  // we just need to check if status exists in the list
  return statuses.some(s => s.status_key === status);
};

// REMOVED: isValidStatusTransition
// Open transitions: any status can transition to any other status
// Validation is only at UI/business logic level, not database level

// Status state checks using database data
export const isFinalStatus = (
  status: string,
  statuses: BookingStatus[] // Use the hook's interface
): boolean => {
  // Common final statuses - but check against actual data
  const commonFinalStatuses = ['delivered_at_destination', 'cancelled', 'quote_was_rejected', 'completed'];
  return commonFinalStatuses.includes(status) && isValidStatus(status, statuses);
};

export const requiresCustomerAction = (
  status: string,
  statuses: BookingStatus[] // Use the hook's interface
): boolean => {
  const category = getStatusCategory(status, statuses);
  // Quotation and payment categories typically require customer action
  return category === 'quotation' || category === 'payment';
};

export const requiresAdminAction = (
  status: string,
  statuses: BookingStatus[] // Use the hook's interface
): boolean => {
  const category = getStatusCategory(status, statuses);
  // Booking category typically requires admin action
  return category === 'booking';
};